import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Disc,
  Coins,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Save,
  Loader2,
  Users,
  RefreshCw,
  Plus,
  Trash2,
  RotateCw,
  Clock,
  Play,
  Search,
  ChevronLeft,
  ChevronRight,
  Sliders,
  Check,
} from "lucide-react";
import API from "../../api/api";

const PRESET_COLORS = [
  "#4F46E5", // Indigo
  "#06B6D4", // Cyan
  "#10B981", // Emerald
  "#F59E0B", // Amber
  "#EC4899", // Pink
  "#8B5CF6", // Purple
  "#3B82F6", // Blue
  "#EF4444", // Red
];

export default function SpinWheel() {
  const [setting, setSetting] = useState({
    isEnabled: true,
    dailyLimit: 1,
    resetRule: "daily_midnight",
    cooldownHours: 24,
    slices: [
      { coins: 10, label: "10 Coins", probability: 30, color: "#4F46E5", isActive: true },
      { coins: 20, label: "20 Coins", probability: 25, color: "#06B6D4", isActive: true },
      { coins: 50, label: "50 Coins", probability: 20, color: "#10B981", isActive: true },
      { coins: 100, label: "100 Coins", probability: 12, color: "#F59E0B", isActive: true },
      { coins: 200, label: "200 Coins", probability: 8, color: "#EC4899", isActive: true },
      { coins: 500, label: "500 Coins", probability: 5, color: "#8B5CF6", isActive: true },
    ],
  });

  const [stats, setStats] = useState({
    totalSpins: 0,
    totalCoinsAwarded: 0,
    uniqueUsersCount: 0,
    todaySpins: 0,
    todayCoins: 0,
  });

  const [history, setHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [historySearch, setHistorySearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Visual Wheel Animation state
  const [isSpinning, setIsSpinning] = useState(false);
  const [wheelRotation, setWheelRotation] = useState(0);
  const [previewWinner, setPreviewWinner] = useState(null);

  // Postman / User API simulator state
  const [simUserId, setSimUserId] = useState("");
  const [simLoading, setSimLoading] = useState(false);
  const [simResult, setSimResult] = useState(null);

  // Clean Old Logs state
  const [isPurgeModalOpen, setIsPurgeModalOpen] = useState(false);
  const [purgeDays, setPurgeDays] = useState(60);
  const [purging, setPurging] = useState(false);

  const timerRef = useRef(null);

  const showNotification = useCallback((msg, isErr = false) => {
    if (isErr) {
      setError(msg);
      setSuccessMsg("");
    } else {
      setSuccessMsg(msg);
      setError("");
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setError("");
      setSuccessMsg("");
    }, 4000);
  }, []);

  const fetchData = useCallback(async (page = 1, search = "") => {
    try {
      setLoading(true);
      setError("");

      const [settingRes, statsRes, historyRes] = await Promise.all([
        API.get("/admin/spin-wheel"),
        API.get("/admin/spin-wheel/stats"),
        API.get(`/admin/spin-wheel/history?page=${page}&limit=10&search=${encodeURIComponent(search)}`),
      ]);

      if (settingRes.data?.success && settingRes.data?.setting) {
        setSetting({
          isEnabled: settingRes.data.setting.isEnabled ?? true,
          dailyLimit: settingRes.data.setting.dailyLimit ?? 1,
          resetRule: settingRes.data.setting.resetRule || "daily_midnight",
          cooldownHours: settingRes.data.setting.cooldownHours ?? 24,
          slices: settingRes.data.setting.slices || [],
        });
      }

      if (statsRes.data?.success) {
        setStats(statsRes.data.stats || {
          totalSpins: 0,
          totalCoinsAwarded: 0,
          uniqueUsersCount: 0,
          todaySpins: 0,
          todayCoins: 0,
        });
      }

      if (historyRes.data?.success) {
        setHistory(historyRes.data.records || []);
        setHistoryPagination(historyRes.data.pagination || { page: 1, limit: 10, total: 0, totalPages: 1 });
      }
    } catch (err) {
      console.error("Failed to load spin wheel data:", err);
      showNotification(err?.response?.data?.message || err.message || "Failed to load configuration", true);
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchData(1, "");
  }, [fetchData]);

  // Quick 1-Click Toggle for Enable / Disable
  const handleQuickToggle = async () => {
    try {
      setToggling(true);
      const nextState = !setting.isEnabled;
      const { data } = await API.put("/admin/spin-wheel", { isEnabled: nextState });
      if (data?.success) {
        setSetting((prev) => ({ ...prev, isEnabled: nextState }));
        showNotification(`Spin the Wheel has been ${nextState ? "ENABLED" : "DISABLED"} successfully!`);
      }
    } catch (err) {
      showNotification(err?.response?.data?.message || err.message || "Failed to toggle status", true);
    } finally {
      setToggling(false);
    }
  };

  // Slices Editor Handlers
  const handleAddSlice = () => {
    const nextColor = PRESET_COLORS[setting.slices.length % PRESET_COLORS.length];
    const newSlice = {
      coins: 50,
      label: "50 Coins",
      probability: 10,
      color: nextColor,
      isActive: true,
    };
    setSetting((prev) => ({ ...prev, slices: [...prev.slices, newSlice] }));
  };

  const handleUpdateSlice = (index, field, value) => {
    setSetting((prev) => {
      const updated = [...prev.slices];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, slices: updated };
    });
  };

  const handleDeleteSlice = (index) => {
    if (setting.slices.length <= 2) {
      showNotification("A minimum of 2 slices is required for the wheel", true);
      return;
    }
    setSetting((prev) => ({
      ...prev,
      slices: prev.slices.filter((_, i) => i !== index),
    }));
  };

  // Auto-normalize probabilities to sum to 100
  const handleAutoNormalizeProbabilities = () => {
    const activeSlices = setting.slices.filter((s) => s.isActive);
    if (activeSlices.length === 0) return;

    const totalWeight = activeSlices.reduce((sum, s) => sum + (Number(s.probability) || 1), 0);
    if (totalWeight <= 0) return;

    let distributed = 0;
    const updatedSlices = setting.slices.map((slice, idx) => {
      if (!slice.isActive) return slice;
      const calculated = Math.floor(((Number(slice.probability) || 1) / totalWeight) * 100);
      distributed += calculated;
      return { ...slice, probability: calculated };
    });

    // Add remainder to first active slice so exact sum is 100
    const remainder = 100 - distributed;
    const firstActiveIndex = updatedSlices.findIndex((s) => s.isActive);
    if (firstActiveIndex !== -1 && remainder > 0) {
      updatedSlices[firstActiveIndex].probability += remainder;
    }

    setSetting((prev) => ({ ...prev, slices: updatedSlices }));
    showNotification("Slice probabilities normalized to exactly 100%!");
  };

  // Save entire settings
  const handleSaveSettings = async (e) => {
    e?.preventDefault?.();
    try {
      setSaving(true);
      setError("");

      // Validate
      if (setting.slices.length < 2) {
        showNotification("Please configure at least 2 wheel slices", true);
        setSaving(false);
        return;
      }

      for (let i = 0; i < setting.slices.length; i++) {
        const s = setting.slices[i];
        if (s.coins === undefined || isNaN(Number(s.coins)) || Number(s.coins) < 0) {
          showNotification(`Slice #${i + 1} must have a valid coin amount`, true);
          setSaving(false);
          return;
        }
        if (!s.label || !s.label.trim()) {
          showNotification(`Slice #${i + 1} must have a label`, true);
          setSaving(false);
          return;
        }
      }

      const payload = {
        isEnabled: setting.isEnabled,
        dailyLimit: Number(setting.dailyLimit),
        resetRule: setting.resetRule,
        cooldownHours: Number(setting.cooldownHours),
        slices: setting.slices,
      };

      const { data } = await API.put("/admin/spin-wheel", payload);
      if (data?.success) {
        showNotification("Spin the Wheel settings saved successfully!");
        fetchData(historyPagination.page, historySearch);
      }
    } catch (err) {
      showNotification(err?.response?.data?.message || err.message || "Failed to save configuration", true);
    } finally {
      setSaving(false);
    }
  };

  // Test Wheel Animation (Pure visual simulation)
  const handleTestSpinPreview = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setPreviewWinner(null);

    const activeSlices = setting.slices.filter((s) => s.isActive);
    if (activeSlices.length === 0) {
      setIsSpinning(false);
      return;
    }

    // Pick random slice
    const randomIndex = Math.floor(Math.random() * activeSlices.length);
    const winning = activeSlices[randomIndex];

    // Calculate rotation: 5 full rotations (1800 deg) + angle to slice
    const sliceAngle = 360 / activeSlices.length;
    // Pointer is at top (270 deg)
    const targetSliceCenter = randomIndex * sliceAngle + sliceAngle / 2;
    const additionalTurns = 5 * 360;
    const finalRotation = wheelRotation + additionalTurns + (360 - (wheelRotation % 360)) + (360 - targetSliceCenter + 270) % 360;

    setWheelRotation(finalRotation);

    setTimeout(() => {
      setIsSpinning(false);
      setPreviewWinner(winning);
    }, 3500);
  };

  // Search History
  const handleHistorySearch = (e) => {
    e.preventDefault();
    fetchData(1, historySearch);
  };

  // Clean Old Logs Handler
  const handlePurgeHistory = async () => {
    try {
      setPurging(true);
      const { data } = await API.post("/admin/spin-wheel/history/purge", { days: purgeDays });
      if (data?.success) {
        showNotification(data.message || `Successfully cleaned spin logs older than ${purgeDays} days.`);
        setIsPurgeModalOpen(false);
        fetchData(1, historySearch);
      } else {
        showNotification(data?.message || "Failed to purge spin history", true);
      }
    } catch (err) {
      showNotification(err?.response?.data?.message || err.message || "Failed to purge spin history", true);
    } finally {
      setPurging(false);
    }
  };

  // Total probability of active slices
  const activeSlices = setting.slices.filter((s) => s.isActive);
  const totalProbability = activeSlices.reduce((sum, s) => sum + (Number(s.probability) || 0), 0);

  return (
    <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-indigo-900/90 via-slate-900 to-purple-900/90 rounded-2xl p-6 border border-indigo-500/20 shadow-xl backdrop-blur-md relative overflow-hidden">
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 -mb-16 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 rounded-xl bg-indigo-500/20 border border-indigo-400/30 text-indigo-400">
                <Disc className="w-6 h-6 animate-spin-slow" />
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
                Spin the Wheel
              </h1>
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider border transition-all ${
                  setting.isEnabled
                    ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 shadow-[0_0_12px_rgba(16,185,129,0.3)]"
                    : "bg-rose-500/20 text-rose-400 border-rose-500/30"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${setting.isEnabled ? "bg-emerald-400 animate-pulse" : "bg-rose-400"}`} />
                {setting.isEnabled ? "Feature Active" : "Feature Disabled"}
              </span>
            </div>
            <p className="text-gray-300 text-sm max-w-2xl">
              Configure daily coin rewards, reward slices, redeem cycles, and winning probabilities for user daily spins.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {/* 1-Click Feature Toggle Switch */}
            <div className="flex items-center gap-2 bg-slate-800/80 px-3.5 py-2 rounded-xl border border-white/10 shadow-sm">
              <span className="text-xs font-medium text-gray-300">Quick Toggle:</span>
              <button
                onClick={handleQuickToggle}
                disabled={toggling}
                type="button"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  setting.isEnabled ? "bg-emerald-500" : "bg-gray-600"
                }`}
                title="Toggle Spin the Wheel ON / OFF"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    setting.isEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <button
              onClick={() => fetchData(historyPagination.page, historySearch)}
              disabled={loading}
              className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/10 transition-colors"
              title="Refresh Data"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin text-indigo-400" : ""}`} />
            </button>

            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-medium shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all text-sm"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </button>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 flex items-center gap-3 animate-fadeIn">
          <AlertCircle className="w-5 h-5 shrink-0 text-rose-400" />
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}
      {successMsg && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 flex items-center gap-3 animate-fadeIn">
          <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-400" />
          <span className="text-sm font-medium">{successMsg}</span>
        </div>
      )}

      {/* Analytics KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800/90 rounded-xl p-4 border border-gray-200 dark:border-slate-700/80 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Coins Distributed</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.totalCoinsAwarded?.toLocaleString() || 0}
              </h3>
            </div>
            <div className="p-3 rounded-xl bg-amber-500/10 text-amber-500 border border-amber-500/20">
              <Coins className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 font-medium">
            +{stats.todayCoins?.toLocaleString() || 0} coins awarded today
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800/90 rounded-xl p-4 border border-gray-200 dark:border-slate-700/80 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Wheel Spins</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.totalSpins?.toLocaleString() || 0}
              </h3>
            </div>
            <div className="p-3 rounded-xl bg-indigo-500/10 text-indigo-500 border border-indigo-500/20">
              <RotateCw className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-2 text-xs text-indigo-600 dark:text-indigo-400 font-medium">
            +{stats.todaySpins?.toLocaleString() || 0} spins performed today
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800/90 rounded-xl p-4 border border-gray-200 dark:border-slate-700/80 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Unique Players</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.uniqueUsersCount?.toLocaleString() || 0}
              </h3>
            </div>
            <div className="p-3 rounded-xl bg-cyan-500/10 text-cyan-500 border border-cyan-500/20">
              <Users className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-2 text-xs text-cyan-600 dark:text-cyan-400 font-medium">
            Engaged users claiming daily spins
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800/90 rounded-xl p-4 border border-gray-200 dark:border-slate-700/80 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Active Slices</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {activeSlices.length} / {setting.slices.length}
              </h3>
            </div>
            <div className="p-3 rounded-xl bg-purple-500/10 text-purple-500 border border-purple-500/20">
              <Sparkles className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-2 text-xs text-purple-600 dark:text-purple-400 font-medium">
            Weight Sum: {totalProbability}%
          </div>
        </div>
      </div>

      {/* Main Grid: Settings & Wheel Preview */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Left Column: Rules & Slices (7 cols) */}
        <div className="xl:col-span-7 space-y-6">
          {/* Rules & Limits Card */}
          <div className="bg-white dark:bg-slate-800/90 rounded-2xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-slate-700">
              <Sliders className="w-5 h-5 text-indigo-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Daily Limits & Redeem Rules
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Daily Spins Allowed Per User
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={setting.dailyLimit}
                    onChange={(e) => setSetting({ ...setting, dailyLimit: Math.max(1, parseInt(e.target.value) || 1) })}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/50 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                  <span className="absolute right-3.5 top-2.5 text-xs text-gray-400">spins/cycle</span>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">Default is 1 spin per day per user.</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Redeem / Reset Cycle
                </label>
                <select
                  value={setting.resetRule}
                  onChange={(e) => setSetting({ ...setting, resetRule: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/50 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  <option value="daily_midnight">Daily Midnight Reset (00:00:00)</option>
                  <option value="cooldown_hours">Rolling Cooldown Hours (e.g. every 24h)</option>
                </select>
                <p className="text-[11px] text-gray-500 mt-1">Controls when the user's spin limit resets.</p>
              </div>

              {setting.resetRule === "cooldown_hours" && (
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Cooldown Hours Between Spins
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={setting.cooldownHours}
                      onChange={(e) => setSetting({ ...setting, cooldownHours: Math.max(1, parseInt(e.target.value) || 24) })}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/50 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                    <span className="absolute right-3.5 top-2.5 text-xs text-gray-400">hours</span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">User must wait this many hours after spinning.</p>
                </div>
              )}
            </div>
          </div>

          {/* Reward Slices Manager */}
          <div className="bg-white dark:bg-slate-800/90 rounded-2xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 pb-3 border-b border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Coins className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Wheel Slices & Coin Rewards
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleAutoNormalizeProbabilities}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-100 border border-purple-200 dark:border-purple-500/20 transition-colors"
                  title="Scale active slice weights so their total equals 100%"
                >
                  Auto-Balance to 100%
                </button>
                <button
                  type="button"
                  onClick={handleAddSlice}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 border border-indigo-200 dark:border-indigo-500/20 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Slice
                </button>
              </div>
            </div>

            {/* Probability Balance Indicator Bar */}
            <div className="mb-5 p-3 rounded-xl bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700/60">
              <div className="flex items-center justify-between text-xs mb-1.5 font-medium">
                <span className="text-gray-600 dark:text-gray-400">
                  Active Probability Weight Sum:
                </span>
                <span
                  className={`font-semibold ${
                    totalProbability === 100
                      ? "text-emerald-500"
                      : "text-amber-500"
                  }`}
                >
                  {totalProbability}% {totalProbability === 100 ? "(Balanced)" : "(Recommended: 100%)"}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-slate-700 h-2 rounded-full overflow-hidden flex">
                {activeSlices.map((slice, idx) => (
                  <div
                    key={idx}
                    style={{
                      width: `${totalProbability > 0 ? (slice.probability / totalProbability) * 100 : 0}%`,
                      backgroundColor: slice.color,
                    }}
                    className="h-full transition-all duration-300"
                    title={`${slice.label}: ${slice.probability}%`}
                  />
                ))}
              </div>
            </div>

            {/* Slices List */}
            <div className="space-y-3">
              {setting.slices.map((slice, index) => (
                <div
                  key={index}
                  className={`p-3.5 rounded-xl border transition-all ${
                    slice.isActive
                      ? "bg-gray-50/50 dark:bg-slate-900/40 border-gray-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-slate-600"
                      : "bg-gray-100/40 dark:bg-slate-900/20 border-gray-200 dark:border-slate-800 opacity-60"
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Color chip & Picker */}
                    <div className="relative group shrink-0">
                      <input
                        type="color"
                        value={slice.color}
                        onChange={(e) => handleUpdateSlice(index, "color", e.target.value)}
                        className="w-9 h-9 rounded-lg cursor-pointer border border-white/20 p-0.5 bg-transparent"
                        title="Pick slice color"
                      />
                    </div>

                    {/* Slice Number Badge */}
                    <span className="w-6 text-xs font-semibold text-gray-400 text-center">
                      #{index + 1}
                    </span>

                    {/* Coins input */}
                    <div className="w-24 shrink-0">
                      <label className="block text-[10px] uppercase font-semibold text-gray-500 dark:text-gray-400 mb-0.5">
                        Coins
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={slice.coins}
                        onChange={(e) => handleUpdateSlice(index, "coins", Math.max(0, parseInt(e.target.value) || 0))}
                        className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-xs font-semibold focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                      />
                    </div>

                    {/* Label input */}
                    <div className="flex-1 min-w-[120px]">
                      <label className="block text-[10px] uppercase font-semibold text-gray-500 dark:text-gray-400 mb-0.5">
                        Label
                      </label>
                      <input
                        type="text"
                        value={slice.label}
                        onChange={(e) => handleUpdateSlice(index, "label", e.target.value)}
                        placeholder="e.g. 50 Coins"
                        className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-xs focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                      />
                    </div>

                    {/* Probability % */}
                    <div className="w-24 shrink-0">
                      <label className="block text-[10px] uppercase font-semibold text-gray-500 dark:text-gray-400 mb-0.5">
                        Weight (%)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={slice.probability}
                          onChange={(e) => handleUpdateSlice(index, "probability", Math.max(0, parseInt(e.target.value) || 0))}
                          className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-xs font-semibold focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                        />
                        <span className="absolute right-2 top-1.5 text-[10px] text-gray-400">%</span>
                      </div>
                    </div>

                    {/* Toggle Active */}
                    <div className="flex items-center gap-1.5 shrink-0 pt-3">
                      <button
                        type="button"
                        onClick={() => handleUpdateSlice(index, "isActive", !slice.isActive)}
                        className={`p-1.5 rounded-lg transition-colors text-xs font-medium ${
                          slice.isActive
                            ? "bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20"
                            : "bg-gray-200 dark:bg-slate-700 text-gray-400"
                        }`}
                        title={slice.isActive ? "Slice Active (Click to disable)" : "Slice Inactive (Click to enable)"}
                      >
                        <Check className={`w-3.5 h-3.5 ${slice.isActive ? "opacity-100" : "opacity-30"}`} />
                      </button>

                      {/* Delete */}
                      <button
                        type="button"
                        onClick={() => handleDeleteSlice(index)}
                        disabled={setting.slices.length <= 2}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Delete slice"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column: Visual Wheel Preview & Test Simulation (5 cols) */}
        <div className="xl:col-span-5 space-y-6">
          {/* Interactive Wheel Preview Card */}
          <div className="bg-white dark:bg-slate-800/90 rounded-2xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm flex flex-col items-center">
            <div className="w-full flex items-center justify-between mb-4 pb-3 border-b border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Disc className="w-5 h-5 text-indigo-500" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Live Wheel Preview
                </h2>
              </div>
              <button
                type="button"
                onClick={handleTestSpinPreview}
                disabled={isSpinning || activeSlices.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 hover:bg-indigo-500 text-white shadow-md shadow-indigo-500/20 disabled:opacity-50 transition-all"
              >
                <Play className="w-3.5 h-3.5 fill-current" />
                {isSpinning ? "Spinning..." : "Test Spin"}
              </button>
            </div>

            {/* SVG Interactive Wheel */}
            <div className="relative w-72 h-72 my-4">
              {/* Pointer Needle at Top */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 w-0 h-0 border-l-[14px] border-l-transparent border-r-[14px] border-r-transparent border-t-[24px] border-t-amber-400 drop-shadow-md" />

              {/* Wheel Container with rotation */}
              <div
                className="w-full h-full rounded-full shadow-2xl overflow-hidden border-4 border-slate-900 dark:border-slate-700"
                style={{
                  transform: `rotate(${wheelRotation}deg)`,
                  transition: isSpinning ? "transform 3.5s cubic-bezier(0.15, 0.9, 0.25, 1)" : "none",
                }}
              >
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  {activeSlices.map((slice, index) => {
                    const sliceCount = activeSlices.length;
                    const angle = 360 / sliceCount;
                    const startAngle = index * angle;
                    const endAngle = (index + 1) * angle;

                    // Convert polar to cartesian
                    const radStart = ((startAngle - 90) * Math.PI) / 180;
                    const radEnd = ((endAngle - 90) * Math.PI) / 180;
                    const x1 = 50 + 50 * Math.cos(radStart);
                    const y1 = 50 + 50 * Math.sin(radStart);
                    const x2 = 50 + 50 * Math.cos(radEnd);
                    const y2 = 50 + 50 * Math.sin(radEnd);

                    const largeArcFlag = angle > 180 ? 1 : 0;
                    const pathData = `M 50 50 L ${x1} ${y1} A 50 50 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

                    // Label position
                    const textAngle = startAngle + angle / 2;
                    const radText = ((textAngle - 90) * Math.PI) / 180;
                    const tx = 50 + 32 * Math.cos(radText);
                    const ty = 50 + 32 * Math.sin(radText);

                    return (
                      <g key={index}>
                        <path d={pathData} fill={slice.color} stroke="#0f172a" strokeWidth="0.5" />
                        <text
                          x={tx}
                          y={ty}
                          fill="#ffffff"
                          fontSize="4"
                          fontWeight="bold"
                          textAnchor="middle"
                          dominantBaseline="central"
                          transform={`rotate(${textAngle}, ${tx}, ${ty})`}
                          style={{ textShadow: "0px 1px 2px rgba(0,0,0,0.8)" }}
                        >
                          {slice.coins}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              </div>

              {/* Center Cap Hub */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-gradient-to-tr from-slate-900 to-indigo-900 border-2 border-amber-400 flex items-center justify-center shadow-lg z-10">
                <Coins className="w-5 h-5 text-amber-400" />
              </div>
            </div>

            {/* Preview Winning Result Announcement */}
            {previewWinner && (
              <div className="mt-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-center animate-bounce">
                <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                  🎉 Preview Winner: Won {previewWinner.coins} Coins! ({previewWinner.label})
                </span>
              </div>
            )}
          </div>

          {/* User API Simulation / Testing Card */}
          <div className="bg-white dark:bg-slate-800/90 rounded-2xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm">
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100 dark:border-slate-700">
              <Sparkles className="w-5 h-5 text-purple-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                API Test Simulator
              </h2>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Test user spin execution and verify automatic coin additions and daily limit enforcement.
            </p>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Enter User Phone or Username..."
                  value={simUserId}
                  onChange={(e) => setSimUserId(e.target.value)}
                  className="flex-1 px-3.5 py-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/50 text-gray-900 dark:text-white text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={async () => {
                    if (!simUserId.trim()) {
                      showNotification("Please enter a username or phone number to simulate", true);
                      return;
                    }
                    try {
                      setSimLoading(true);
                      setSimResult(null);
                      // Search user first
                      const userRes = await API.get(`/admin/users?search=${encodeURIComponent(simUserId.trim())}`);
                      const foundUser = userRes.data?.users?.[0] || userRes.data?.data?.[0];
                      if (!foundUser) {
                        showNotification("User not found in system", true);
                        return;
                      }

                      // Execute spin for user by calling status & spin
                      const spinRes = await API.post(`/spin-wheel/spin`, {}, {
                        headers: { "x-simulate-user-id": foundUser._id },
                      });
                      setSimResult({ user: foundUser, ...spinRes.data });
                      fetchData(1, historySearch);
                      showNotification("Simulation executed!");
                    } catch (err) {
                      setSimResult({
                        error: err?.response?.data?.message || err.message || "Simulation error",
                        status: err?.response?.status,
                      });
                    } finally {
                      setSimLoading(false);
                    }
                  }}
                  disabled={simLoading}
                  className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold text-xs transition-colors shrink-0"
                >
                  {simLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Test Spin"}
                </button>
              </div>

              {simResult && (
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-xs font-mono overflow-x-auto">
                  <pre className="text-gray-800 dark:text-gray-300">
                    {JSON.stringify(simResult, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Audit History Table */}
      <div className="bg-white dark:bg-slate-800/90 rounded-2xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4 pb-3 border-b border-gray-100 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-500" />
              Recent User Spin History & Audit Logs
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Live record of users spinning the wheel and winning coins across the platform.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {/* Search */}
            <form onSubmit={handleHistorySearch} className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                <input
                  type="text"
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  placeholder="Search user name or phone..."
                  className="pl-9 pr-3.5 py-1.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/50 text-gray-900 dark:text-white text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none w-52 sm:w-60"
                />
              </div>
              <button
                type="submit"
                className="px-3 py-1.5 rounded-xl bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 text-xs font-medium transition-colors"
              >
                Search
              </button>
            </form>

            {/* Clean Old Logs Button */}
            <button
              type="button"
              onClick={() => setIsPurgeModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-rose-50 dark:bg-rose-500/10 hover:bg-rose-100 dark:hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20 text-xs font-semibold transition-all shadow-sm"
              title="Clean audit logs older than 60 days"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clean Old Logs (60d)
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">
                <th className="py-3 px-3.5 xl:px-4 2xl:px-6">User</th>
                <th className="py-3 px-3.5 xl:px-4 2xl:px-6">Coins Won</th>
                <th className="py-3 px-3.5 xl:px-4 2xl:px-6">Reward / Slice</th>
                <th className="py-3 px-3.5 xl:px-4 2xl:px-6">Wallet Impact</th>
                <th className="py-3 px-3.5 xl:px-4 2xl:px-6">Status</th>
                <th className="py-3 px-3.5 xl:px-4 2xl:px-6">Date & Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {history.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-400">
                    No spin records found.
                  </td>
                </tr>
              ) : (
                history.map((record) => (
                  <tr key={record._id} className="hover:bg-gray-50/50 dark:hover:bg-slate-900/30 transition-colors">
                    <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">
                      <div className="flex items-center gap-2.5">
                        <img
                          src={record.user?.mainImage || record.user?.avatar || "https://avatar.iran.liara.run/public"}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover border border-gray-200 dark:border-slate-700"
                        />
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {record.user?.name || "Anonymous User"}
                          </div>
                          <div className="text-[11px] text-gray-400">
                            @{record.user?.username || "user"} • {record.user?.phone || "No phone"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 font-semibold text-amber-500">
                      <span className="inline-flex items-center gap-1 bg-amber-500/10 px-2.5 py-1 rounded-full border border-amber-500/20">
                        <Coins className="w-3.5 h-3.5" />
                        +{record.coinsAwarded} Coins
                      </span>
                    </td>
                    <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 text-gray-700 dark:text-gray-300 font-medium">
                      {record.rewardLabel || `Slice #${record.sliceIndex + 1}`}
                    </td>
                    <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 text-gray-500 dark:text-gray-400">
                      {record.balanceBefore} ➔ <span className="text-emerald-500 font-semibold">{record.balanceAfter}</span>
                    </td>
                    <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                          record.status === "success"
                            ? "bg-emerald-500/10 text-emerald-500"
                            : "bg-rose-500/10 text-rose-500"
                        }`}
                      >
                        {record.status}
                      </span>
                    </td>
                    <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {new Date(record.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {historyPagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-slate-700 text-xs text-gray-500">
            <span>
              Showing Page {historyPagination.page} of {historyPagination.totalPages} ({historyPagination.total} records)
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => fetchData(historyPagination.page - 1, historySearch)}
                disabled={historyPagination.page <= 1}
                className="p-1.5 rounded-lg border border-gray-200 dark:border-slate-700 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => fetchData(historyPagination.page + 1, historySearch)}
                disabled={historyPagination.page >= historyPagination.totalPages}
                className="p-1.5 rounded-lg border border-gray-200 dark:border-slate-700 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Clean Old Audit Logs Modal */}
      {isPurgeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-slate-700 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">Clean Old Audit Logs</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Permanently remove old spin records</p>
              </div>
            </div>

            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
              This action deletes historical spin records older than the selected timeframe to keep your database fast and lightweight.
              <span className="block mt-1 font-semibold text-amber-600 dark:text-amber-400">
                Note: User wallet coins and balance amounts will NOT be affected.
              </span>
            </p>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Delete records older than:
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 30, label: "30 Days" },
                  { value: 60, label: "60 Days (Default)" },
                  { value: 90, label: "90 Days" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPurgeDays(opt.value)}
                    className={`py-2 px-2.5 rounded-xl text-xs font-medium border text-center transition-all ${
                      purgeDays === opt.value
                        ? "bg-rose-50 dark:bg-rose-500/20 border-rose-500 text-rose-600 dark:text-rose-400 font-semibold"
                        : "bg-gray-50 dark:bg-slate-900/40 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-gray-100 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setIsPurgeModalOpen(false)}
                disabled={purging}
                className="px-4 py-2 text-xs font-medium rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePurgeHistory}
                disabled={purging}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-xl bg-rose-600 hover:bg-rose-700 text-white transition-colors disabled:opacity-50 shadow-md shadow-rose-600/20 cursor-pointer"
              >
                {purging ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Cleaning...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    Clean Logs ({purgeDays}d+)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
